import { getIsOnlineSnapshot } from '@/contexts/onlineStatus';
import { message } from '@/lib/notify/apNotify';
import { SupabaseServices } from '@/providers/supabaseProvider/supabaseServices';
import { CrudFilters, CrudSort } from '@refinedev/core';
import * as Sentry from '@sentry/react';

export type RealtimeDbCreateManyValue = {
  resource: string;
  uuids?: string[];
  data: any;
  actionType?: any;
  options?: {
    createNewDocId?: boolean;
    newDocId?: string;
    conflictTarget?: string; // e.g. 'id' or 'uuid'
    bulkUpsert?: boolean; // if true, attempt array upsert first
  };
};

type SupabaseDataProvider = {
  createMany: (params: { values: RealtimeDbCreateManyValue[]; meta: any }) => Promise<any>;
  getList: (params: {
    resource: string;
    pagination: any;
    sorters: any;
    filters: any;
    meta: any;
    realtimeFilters?: any;
  }) => Promise<any>;
  getOne: (params: {
    resource: string;
    id: string;
    meta: any;
    filters?: CrudFilters;
    realtimeFilters?: any;
  }) => Promise<any>;
  setPresenceData: (params: { resource: string; meta: any }) => Promise<any>;
  getPresenceData: (params: { resource: string; meta: any }) => Promise<any>;
  watchOnlineStatus: (params: { meta: any }) => Promise<any>;
  incrementValue: (previous?: number) => number;
};

/**
 * Drops the DB-generated primary key from a write payload. `id` is owned by the
 * table's identity sequence on every Supabase table; rows snapshotted from reads
 * (getList/getOne return it) must never send it back on a write — an INSERT
 * carrying a stale `id` collides on the table's pkey even when the upsert's
 * onConflict target is `uuid` (APL-11808 TC-24, Postgres 23505 on
 * user_responses_pkey). Every write in this provider targets rows by `uuid`,
 * never by the `id` column, so stripping it is side-effect free.
 */
const stripDbId = (row: any) => {
  const copy = { ...row };
  delete copy.id;
  return copy;
};

/**
 * Drops columns absent from the China Supabase schema. cf964f836a stopped
 * EMITTING `game_status` (user_responses has no such column — Postgres 42703,
 * verified by direct REST probe), but rows persisted before that commit still
 * carry the key in IndexedDB/store, and the write paths spread stored rows
 * verbatim (useRealtimeUserResponses' beforeUpdateUserResponse spread, the
 * merged-hydration write-back) — so legacy rows still 42703 on every write.
 * Stripping here, at the same provider-level seam as stripDbId, covers every
 * caller; the schemaless Firestore path keeps the key. When the column ships,
 * removing it from LEGACY_ABSENT_COLUMNS is the single line to change.
 */
// 'experienceId': legacy VideoCallTool payload key (removed f28828d066) that
// still rides cached user_info rows in IndexedDB/store — 42703s on write-back.
// 'online_priority_keys': legacy field emitted by an old boot write, accumulates
// in Dexie/docs; boot writes now send merged rows so it can ride any write.
const LEGACY_ABSENT_COLUMNS = ['game_status', 'experienceId', 'online_priority_keys'];
const stripLegacyColumns = (row: any) => {
  const copy = { ...row };
  for (const column of LEGACY_ABSENT_COLUMNS) delete copy[column];
  return copy;
};

/**
 * Builds Supabase query filters from Refine CrudFilters
 */
const buildSupabaseFilters = (query: any, filters?: CrudFilters) => {
  if (!filters) return query;

  filters.forEach((filter) => {
    if ('field' in filter) {
      const { field, value } = filter;
      const operator = filter.operator as string;

      switch (operator) {
        case 'eq':
          query = query.eq(field, value);
          break;
        case 'ne':
          query = query.neq(field, value);
          break;
        case 'lt':
          query = query.lt(field, value);
          break;
        case 'gt':
          query = query.gt(field, value);
          break;
        case 'lte':
          query = query.lte(field, value);
          break;
        case 'gte':
          query = query.gte(field, value);
          break;
        case 'in':
          query = query.in(field, value);
          break;
        case 'contains':
          query = query.contains(field, Array.isArray(value) ? value : [value]);
          break;
        case 'containss':
          query = query.ilike(field, `%${value}%`);
          break;
        case 'containedBy':
          query = query.containedBy(field, value);
          break;
        default:
          console.warn(`[SupabaseProvider] Unsupported filter operator: ${filter.operator}`);
      }
    }
  });

  return query;
};

/**
 * Builds Supabase query sorting from Refine CrudSort
 */
const buildSupabaseSorters = (query: any, sorters?: CrudSort[]) => {
  if (!sorters || sorters.length === 0) return query;

  sorters.forEach((sorter) => {
    query = query.order(sorter.field, { ascending: sorter.order === 'asc' });
  });

  return query;
};

/**
 * Supabase Data Provider
 * Provides CRUD operations and realtime functionality using Supabase
 * Similar to firestoreDataProvider but for Supabase/PostgreSQL
 */
export const supabaseDataProvider = (
  supabaseServicesProps?: SupabaseServices | null
): SupabaseDataProvider => {
  const initializedSupabaseServices = supabaseServicesProps;

  if (!initializedSupabaseServices) {
    console.warn('[SupabaseProvider] Supabase services are not initialized');
  }

  const client = initializedSupabaseServices?.client;

  const normalisePresenceState = (presenceState: Record<string, any[]>) => {
    if (!presenceState) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(presenceState).map(([key, presences]) => {
        if (Array.isArray(presences) && presences.length > 0) {
          return [key, presences[presences.length - 1]];
        }
        return [key, undefined];
      })
    );
  };

  return {
    /**
     * Watch online status (using Supabase Realtime presence)
     * @param meta.channelName - Channel name to watch (default: 'online-status')
     * @param meta.onRealtimeEvent - Callback for realtime events
     */
    watchOnlineStatus: async ({ meta }) => {
      if (!client) throw new Error('Supabase client not initialized');

      const channelName = meta?.channelName || 'online-status';
      const channel = client.channel(channelName);

      channel.on('presence', { event: 'sync' }, () => {
        const state = channel.presenceState();
        if (meta?.onRealtimeEvent) {
          meta.onRealtimeEvent('ONLINE_STATUS', state);
        }
      });

      await channel.subscribe();

      const unsubscribe = () => {
        channel.unsubscribe();
      };

      return {
        meta: { unsubscribe },
      };
    },

    /**
     * Increment a value, optionally from a given previous value.
     * @param previous - (optional) The previous value to increment.
     * @returns number - The incremented value (previous + 1, or 1 if previous is undefined/null).
     */
    incrementValue: (previous?: number) => {
      return previous ? previous + 1 : 1;
    },

    /**
     * Get presence data from Supabase Realtime
     * @param resource - Channel name/resource for presence tracking
     * @param meta.onRealtimeEvent - Callback for realtime events (sync, join, leave)
     */
    getPresenceData: async ({ resource, meta }) => {
      if (!client) throw new Error('Supabase client not initialized');

      const channel = client.channel(resource);

      // Standard presence API - fixed in supabase-js v2.55+
      channel.on('presence', { event: 'sync' }, () => {
        const presenceData = normalisePresenceState(channel.presenceState());
        meta?.onRealtimeEvent?.('PRESENCE_DATA', presenceData);
      });

      channel.on('presence', { event: 'join' }, ({ key, newPresences }) => {
        // Process join events
        if (newPresences && newPresences.length > 0) {
          const latestPresence = newPresences[newPresences.length - 1];
          meta?.onRealtimeEvent?.('ADDED', { key, presence: latestPresence });
        }
      });

      channel.on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
        // Process leave events
        if (leftPresences && leftPresences.length > 0) {
          const latestPresence = leftPresences[leftPresences.length - 1];
          meta?.onRealtimeEvent?.('REMOVED', { key, presence: latestPresence });
        }
      });

      // Subscribe AFTER registering listeners
      await channel.subscribe(async (status) => {
        if (status === 'SUBSCRIBED' && meta?.presencePayload) {
          // Track this user's presence (required for presenceState to have data)
          await channel.track(meta.presencePayload);
        }
      });

      const presenceData = normalisePresenceState(channel.presenceState());

      const unsubscribe = () => {
        channel.untrack();
        channel.unsubscribe();
      };

      return {
        data: presenceData,
        meta: { unsubscribe },
      };
    },

    /**
     * Set presence data in Supabase Realtime
     * @param resource - Channel name/resource for presence tracking
     * @param meta.presencePayload - Custom payload to track (default: { state: 'online', last_changed: timestamp })
     */
    setPresenceData: async ({ resource, meta }) => {
      if (!client) throw new Error('Supabase client not initialized');

      const presenceKey = meta?.presenceKey;
      const channelOptions = presenceKey
        ? { config: { presence: { key: presenceKey } } }
        : undefined;
      const channel = client.channel(resource, channelOptions);

      const buildPresencePayload = (state: 'online' | 'offline') => ({
        ...meta?.presencePayload,
        state,
        last_changed: new Date().toISOString(),
      });

      const logPresence = (payload: any) => {
        if (presenceKey) {
          console.log('Supabase presence track with key', presenceKey, payload);
        }
      };

      let hasTrackedOnline = false;
      let trackOnlinePromise: Promise<void> | null = null;

      const trackPresence = async (
        payload: any,
        { markOnline = false }: { markOnline?: boolean } = {}
      ) => {
        logPresence(payload);
        await channel.track(payload);
        if (markOnline) {
          hasTrackedOnline = true;
        }
      };

      const startOnlineTracking = () => {
        trackOnlinePromise = trackPresence(buildPresencePayload('online'), {
          markOnline: true,
        }).catch((error) => {
          console.error('Supabase presence track failed', error);
        });
      };

      // Subscribe and track presence
      channel.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          startOnlineTracking();
        }
      });

      const unsubscribe = async () => {
        if (trackOnlinePromise) {
          try {
            await trackOnlinePromise;
          } catch (error) {
            console.error('Supabase presence online track pending', error);
          }
        }

        if (hasTrackedOnline) {
          try {
            await trackPresence(buildPresencePayload('offline'));
          } catch (error) {
            console.error('Supabase presence offline track failed', error);
          }

          try {
            await channel.untrack();
          } catch (error) {
            console.error('Supabase presence untrack failed', error);
          }
        }

        try {
          await channel.unsubscribe();
        } catch (error) {
          console.error('Supabase presence unsubscribe failed', error);
        }
      };

      return {
        data: null,
        meta: { unsubscribe },
      };
    },

    /**
     * Get a single record by ID
     * @param meta.select - Fields to select (default: '*')
     * @param meta.idField - Field name to filter by (default: 'uuid')
     * @param meta.filters - Additional filters to apply
     * @param meta.realtimeFilter - Custom filter string for realtime subscription
     * @param meta.realtimeEvent - Event type to listen for (default: 'UPDATE', can be 'INSERT', 'DELETE', '*')
     * @param meta.realtimeSchema - Schema name (default: 'public')
     * @param meta.onRealtimeEvent - Callback for realtime events
     */
    getOne: async ({ resource, id, meta, filters, realtimeFilters }) => {
      console.log('getOne', resource, id, meta);
      if (!client) throw new Error('Supabase client not initialized');

      const docPath = `${resource}/${id}`;
      console.log('getOne docPath', docPath);

      // Extract params from meta with defaults
      const selectFields = meta?.select || '*';
      console.log('selectFields ====>', meta);
      const additionalFilters = filters; // Use the new filters parameter

      // Build query
      let baseQuery: any = client.from(resource).select(selectFields);

      // Apply additional filters if provided
      if (additionalFilters) {
        baseQuery = buildSupabaseFilters(baseQuery, additionalFilters);
      }
      console.log('baseQuery ====>', baseQuery);
      const { data, error } = await baseQuery.maybeSingle();

      if (error) throw error;

      const finalData = data ? { ...data } : {};

      let unsubscribe: (() => void) | undefined = undefined;

      // Set up realtime subscription if requested. Deliberately NOT gated on
      // finalData.id: the realtime filter is uuid-based, so a not-yet-existing
      // row (e.g. user_info before the first teacher grant) still gets a valid
      // channel and its later INSERT reaches the subscriber. R3-TJ5, 2026-07-24.
      if (meta?.onRealtimeEvent) {
        // Build filter string for the specific record
        // Format: "column_name=eq.value"
        let filterString: string | undefined;
        if (Array.isArray(realtimeFilters)) {
          const normalizedFilters = (realtimeFilters as any[]).filter(
            (filter: any) =>
              filter &&
              filter.field &&
              filter.operator &&
              filter.value !== undefined &&
              filter.value !== null
          );
          filterString = normalizedFilters
            .map((filter: any) => `${filter.field}=${filter.operator}.${filter.value}`)
            .join('&');
          if (filterString.length === 0) {
            filterString = undefined;
          }
        } else if (typeof realtimeFilters === 'string') {
          const trimmedFilter = realtimeFilters.trim();
          filterString = trimmedFilter.length > 0 ? trimmedFilter : undefined;
        }

        if (!filterString) {
          filterString = `id=eq.${id}`;
        }

        console.log(
          'realtimeFilters check ====>',
          realtimeFilters,
          'computed filter:',
          filterString
        );

        const channelConfig: any = {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: meta?.realtimeSchema || 'public',
          table: resource,
          filter: filterString, // This is crucial - filter for specific record
        };

        const channelName = filterString
          ? `public:${resource}:${filterString}`
          : `public:${resource}`;
        console.log('channelName check ====>', channelName, client, channelConfig);

        const channel = client
          .channel(channelName)
          .on('postgres_changes', channelConfig, (payload) => {
            console.log('✅ Realtime payload received:', payload);

            // Handle different event types
            if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
              const updatedData = payload.new ? { ...payload.new, id } : null;
              if (updatedData) {
                meta.onRealtimeEvent('UPDATED', updatedData);
              }
            } else if (payload.eventType === 'DELETE') {
              const deletedData = payload.old ? { ...payload.old, id } : null;
              if (deletedData) {
                meta.onRealtimeEvent('REMOVED', deletedData);
              }
            }
          })
          .subscribe((status, err) => {
            if (status === 'SUBSCRIBED') {
              console.log('✅ Successfully subscribed to realtime updates for:', channelName);
              // Empty fetch ⇒ nothing to replay; the row's INSERT event will arrive.
              if (finalData.id) {
                meta.onRealtimeEvent('UPDATED', finalData);
              }
            } else if (status === 'CHANNEL_ERROR') {
              Sentry.captureException(err);
              console.error('❌ Realtime subscription error:', err, channelName, resource);
            } else if (status === 'TIMED_OUT') {
              Sentry.captureException(err);
              console.error('❌ Realtime subscription timed out');
            } else {
              Sentry.captureException(err);
              console.log('📡 Realtime subscription status:', status);
            }
          });

        console.log('📡 Setting up realtime channel:', channelName, 'with filter:', filterString);

        unsubscribe = () => {
          console.log('🔌 Unsubscribing from realtime channel:', channelName);
          channel.unsubscribe();
        };
      }

      return {
        data: finalData,
        meta: {
          unsubscribe,
        },
      };
    },

    /**
     * Get a list of records with pagination, filtering, and sorting
     * @param meta.select - Fields to select (default: '*')
     * @param meta.countType - Count type for pagination: 'exact' | 'planned' | 'estimated' (default: 'exact')
     * @param meta.realtimeSchema - Schema name for realtime (default: 'public')
     * @param meta.realtimeFilter - Custom filter string for realtime subscription
     * @param meta.realtime - Enable realtime subscription (default: false)
     * @param meta.onRealtimeEvent - Callback for realtime events
     */
    getList: async ({ resource, pagination, filters, sorters, meta, realtimeFilters }) => {
      console.log('getList', resource, pagination, sorters, filters, meta);
      if (!client) throw new Error('Supabase client not initialized');

      const { current = 1, pageSize = 10 } = pagination || {};
      const from = (current - 1) * pageSize;
      const to = from + pageSize - 1;

      // Extract params from meta with defaults
      const selectFields = meta?.select || '*';
      const countType = meta?.countType || 'exact';
      const realtimeSchema = meta?.realtimeSchema || 'public';

      // Build query
      let query = client.from(resource).select(selectFields, { count: countType });
      if (filters) {
        // Apply filters
        query = buildSupabaseFilters(query, filters);
      }
      // Apply sorting
      query = buildSupabaseSorters(query, sorters);

      // Apply pagination
      if (pagination) {
        if (current > 1) {
          console.log(
            `[SupabaseProvider] Fetching page ${current} for ${resource} (range: ${from}-${to})`
          );
        }
        query = query.range(from, to);
      }

      const { data, error, count } = await query;

      if (error) throw error;

      const initialData = (data || []).map((doc: any) => {
        console.log('data supabase - datum', doc);
        return { ...doc, id: doc.id };
      });

      const total: number | undefined = count || undefined;

      let unsubscribeFn: (() => void) | undefined = undefined;
      if (meta?.realtime && meta?.onRealtimeEvent) {
        const baseConfig = {
          schema: realtimeSchema,
          table: resource,
        };

        let filterString: string | undefined;
        if (Array.isArray(realtimeFilters)) {
          filterString = realtimeFilters
            .filter(
              (filter) =>
                filter &&
                filter.field &&
                filter.operator &&
                filter.value !== undefined &&
                filter.value !== null
            )
            .map((filter) => `${filter.field}=${filter.operator}.${filter.value}`)
            .join('&');
          if (filterString.length === 0) {
            filterString = undefined;
          }
        } else if (typeof realtimeFilters === 'string') {
          const trimmedFilter = realtimeFilters.trim();
          filterString = trimmedFilter.length > 0 ? trimmedFilter : undefined;
        }

        const channelName = filterString ? `${resource}-list:${filterString}` : `${resource}-list`;
        const realtimeConfig = filterString
          ? { ...baseConfig, filter: filterString }
          : { ...baseConfig };

        const channel = client
          .channel(channelName)
          .on('postgres_changes', { event: 'INSERT', ...realtimeConfig }, (payload: any) => {
            const docWithId = { ...payload.new, id: payload.new.id };
            meta.onRealtimeEvent('ADDED', docWithId);
          })
          .on('postgres_changes', { event: 'UPDATE', ...realtimeConfig }, (payload: any) => {
            const docWithId = { ...payload.new, id: payload.new.id };
            meta.onRealtimeEvent('UPDATED', docWithId);
          })
          .on('postgres_changes', { event: 'DELETE', ...realtimeConfig }, (payload: any) => {
            const docWithId = { ...payload.old, id: payload.old.id };
            meta.onRealtimeEvent('REMOVED', docWithId);
          })
          .subscribe();

        unsubscribeFn = () => {
          // console.log(`[SupabaseProvider] Unsubscribing from REALTIME listener for ${resource}.`);
          channel.unsubscribe();
        };
      }

      const finalResponse = {
        data: initialData,
        total: total,
        meta: {
          unsubscribeFn,
        },
      };

      return finalResponse;
    },

    /**
     * Create or update multiple records (batch operation)
     */
    createMany: async ({ values, meta }) => {
      console.log('createMany values ==>', values, meta);
      console.log('createMany values.length ==>', values.length);

      if (!client) throw new Error('Supabase client not initialized');

      const results: any[] = [];
      const returningColumnsRaw = meta?.columns ?? meta?.select;
      const returningColumns = Array.isArray(returningColumnsRaw)
        ? returningColumnsRaw.join(',')
        : typeof returningColumnsRaw === 'string' && returningColumnsRaw.trim().length > 0
        ? returningColumnsRaw
        : undefined;

      try {
        // Calculate total operations for logging
        let totalOperations = 0;
        for (const value of values) {
          if (value.uuids) {
            totalOperations += value.uuids.length;
          } else {
            totalOperations += 1;
          }
        }
        console.log('createMany total operations:', totalOperations);

        for (let i = 0; i < values.length; i++) {
          const transform = meta?.transformRealtimeDBProps;
          const value = transform ? transform(values[i]) : values[i];
          const actionType = value.actionType || 'set';
          console.log(`createMany processing value ${i + 1}/${values.length}:`, value);

          try {
            if (value.uuids) {
              console.log(
                `createMany value.uuids found, processing ${value.uuids.length} uuids:`,
                value.uuids
              );

              const conflictTarget: any = value.options?.conflictTarget || 'uuid';
              if (value.options?.bulkUpsert && actionType !== 'delete') {
                // Try bulk upsert first
                const objectsToUpsert: any[] = [];
                for (const uuid of value.uuids) {
                  const newObject: any = stripLegacyColumns(stripDbId(value.data));
                  if (!newObject[conflictTarget]) newObject[conflictTarget] = uuid;
                  if (
                    value.resource === 'messages' &&
                    value.options?.createNewDocId &&
                    newObject.msg_type === 'announcement' &&
                    typeof newObject.uuid === 'string'
                  ) {
                    newObject.subscription_key = newObject.uuid.split('_____')[0];
                  }
                  newObject.updated_at = newObject.updated_at || new Date().toISOString();
                  objectsToUpsert.push(newObject);
                }
                try {
                  const bulkUpsertQuery = client
                    .from(value.resource)
                    .upsert(objectsToUpsert, { onConflict: conflictTarget });
                  const { error } = returningColumns
                    ? await bulkUpsertQuery.select(returningColumns)
                    : await bulkUpsertQuery;
                  if (error) throw error;
                  for (const uuid of value.uuids) {
                    results.push({
                      success: true,
                      resource: value.resource,
                      uuid,
                      action: 'set',
                    });
                  }
                } catch (bulkError: any) {
                  if (
                    typeof bulkError?.message === 'string' &&
                    bulkError.message.includes('All object keys must match')
                  ) {
                    // Fallback to single row upserts
                    for (const objectToUpsert of objectsToUpsert) {
                      const singleQuery = client
                        .from(value.resource)
                        .upsert([objectToUpsert], { onConflict: conflictTarget });
                      const { error } = returningColumns
                        ? await singleQuery.select(returningColumns)
                        : await singleQuery;
                      if (error) {
                        results.push({
                          success: false,
                          resource: value.resource,
                          uuid: objectToUpsert[conflictTarget],
                          error,
                        });
                      } else {
                        results.push({
                          success: true,
                          resource: value.resource,
                          uuid: objectToUpsert[conflictTarget],
                          action: 'set',
                        });
                      }
                    }
                  } else {
                    throw bulkError;
                  }
                }
              } else {
                // Process all IDs with the same data sequentially
                for (const uuid of value.uuids) {
                  console.log(`createMany processing uuid ${uuid}:`, uuid);
                  if (value.data) {
                    console.log(
                      'createMany value.data exists, actionType:',
                      value.actionType,
                      value
                    );
                    if (actionType === 'delete') {
                      console.log('createMany executing delete');
                      const { error } = await (client.from(value.resource).delete() as any).eq(
                        conflictTarget,
                        uuid
                      );
                      if (error) throw error;
                      results.push({
                        success: true,
                        resource: value.resource,
                        uuid,
                        action: 'delete',
                      });
                    } else if (actionType === 'update') {
                      console.log('createMany executing update');
                      const updateQuery = (
                        client
                          .from(value.resource)
                          .update(stripLegacyColumns(stripDbId(value.data))) as any
                      ).eq(conflictTarget, uuid);
                      const { error } = returningColumns
                        ? await updateQuery.select(returningColumns)
                        : await updateQuery;
                      if (error) throw error;
                      results.push({
                        success: true,
                        resource: value.resource,
                        uuid,
                        action: 'update',
                      });
                    } else if (actionType === 'set') {
                      console.log('createMany executing upsert (set)');
                      const dataToUpsert: any = stripLegacyColumns(stripDbId(value.data));
                      if (!dataToUpsert[conflictTarget]) dataToUpsert[conflictTarget] = uuid;
                      if (
                        value.resource === 'messages' &&
                        value.options?.createNewDocId &&
                        dataToUpsert.msg_type === 'announcement' &&
                        typeof dataToUpsert.uuid === 'string'
                      ) {
                        dataToUpsert.subscription_key = dataToUpsert.uuid.split('_____')[0];
                      }
                      const upsertQuery = client
                        .from(value.resource)
                        .upsert([dataToUpsert], { onConflict: conflictTarget });
                      const { error } = returningColumns
                        ? await upsertQuery.select(returningColumns)
                        : await upsertQuery;
                      if (error) throw error;
                      results.push({
                        success: true,
                        resource: value.resource,
                        uuid,
                        action: 'set',
                      });
                    }
                  } else {
                    console.log('createMany value.data is null/undefined, skipping');
                  }
                }
              }
              console.log(
                `createMany completed processing all ${value.uuids.length} uuids for value ${i + 1}`
              );
            } else {
              console.log('createMany no value.uuids, processing single document');

              if (value.data) {
                console.log('createMany value.data exists', value.data);

                // Add timestamp for single documents
                const dataWithTimestamp = {
                  ...value.data,
                  updated_at: value.data.updated_at || new Date().toISOString(),
                };
                console.log('createMany dataWithTimestamp', dataWithTimestamp);

                // Determine ID - support both createNewDocId and newDocId options
                const conflictTarget = 'uuid';
                const id = value.options?.createNewDocId || value.options?.newDocId;

                if (actionType === 'delete') {
                  console.log('createMany executing delete');
                  if (id) {
                    const { error } = await client
                      .from(value.resource)
                      .delete()
                      .eq(conflictTarget, id);
                    if (error) throw error;
                    results.push({
                      success: true,
                      resource: value.resource,
                      id,
                      action: 'delete',
                    });
                  }
                } else if (actionType === 'update') {
                  console.log('createMany executing update');
                  if (id) {
                    const updateQuery = client
                      .from(value.resource)
                      .update(stripLegacyColumns(stripDbId(dataWithTimestamp)))
                      .eq(conflictTarget, id);
                    const { error } = returningColumns
                      ? await updateQuery.select(returningColumns)
                      : await updateQuery;
                    if (error) throw error;
                    results.push({
                      success: true,
                      resource: value.resource,
                      id,
                      action: 'update',
                    });
                  }
                } else {
                  console.log('createMany executing upsert (set)');
                  const strippedData = stripLegacyColumns(stripDbId(dataWithTimestamp));
                  const dataToInsert = id
                    ? { [conflictTarget]: id, ...strippedData }
                    : strippedData;
                  const upsertQuery = client
                    .from(value.resource)
                    .upsert([dataToInsert], { onConflict: conflictTarget });
                  const { error } = returningColumns
                    ? await upsertQuery.select(returningColumns)
                    : await upsertQuery;
                  if (error) throw error;
                  results.push({
                    success: true,
                    resource: value.resource,
                    id,
                    action: 'set',
                  });
                }
              } else {
                console.log('createMany value.data is null/undefined, skipping');
              }
            }
            console.log(`createMany completed processing value ${i + 1}/${values.length}`);
          } catch (valueError) {
            console.error(
              `createMany error processing value ${i + 1}/${values.length}:`,
              valueError
            );
            // Capture the row's stable identifier so the Sentry message names the
            // failing response (eId…_uId…_sId…), not `#unknown` (APL-11925 #4).
            results.push({
              success: false,
              resource: value.resource,
              // user_info batches carry their key in value.uuids, not value.data.
              uuid: value.data?.uuid ?? value.data?.user_response_id ?? value.uuids?.[0],
              error: valueError,
            });
          }
        }

        // Any row failure must throw so useRealtimeDbBatchMutation reports
        // `ok: false` (and captures to Sentry once), mirroring firestoreDataProvider's
        // atomic contract — otherwise a failed save silently shows "Saved" (APL-11925 #2/#4).
        const failedResults = results.filter((r) => r.success === false);
        if (failedResults.length > 0) {
          const region = import.meta.env.VITE_REGION;
          const describeFailure = (r: any) => {
            const identifier = r.uuid ?? r.id ?? 'unknown';
            const rowError = r.error;
            const detail =
              rowError?.message || rowError?.details || rowError?.code || String(rowError);
            return `${r.resource}#${identifier}: ${detail}`;
          };
          const aggregateError: any = new Error(
            `Supabase createMany failed for ${failedResults.length}/${results.length} operation(s)` +
              ` [region=${region}]: ${failedResults.map(describeFailure).join('; ')}`
          );
          // The resource#uuid + error context rides in the message above (that is
          // what reaches Sentry). failures/region are attached for any local
          // handler that inspects the thrown error (APL-11925 #4).
          aggregateError.failures = failedResults;
          aggregateError.region = region;
          throw aggregateError;
        }

        if (meta?.onSuccess) {
          console.log('createMany meta.onSuccess', results);
          meta.onSuccess(results);
        }
        console.log('createMany all operations completed successfully');

        return {
          success: true,
          data: results,
        };
      } catch (error: any) {
        if (meta?.onError) {
          meta.onError(error);
        }
        // APL-12236 CN7-34: this is a DIAGNOSTIC toast (dev/staging only) and it
        // prints the raw error — resource#uuid, region, and the Supabase host.
        // A dropped connection makes every queued write fail at once, so a
        // student mid-exam on staging got a wall of them. Offline is an expected
        // condition, not a finding, so it earns no toast; Sentry and the console
        // still get everything, and a genuine failure while ONLINE still shows.
        //
        // Uses the app's OWN online signal, not navigator.onLine: the latter is
        // the raw browser flag everywhere except iOS (useOverrideNavigator
        // patches it for AAC mode only, APL-5761), so it stays true through a
        // ping timeout / network_issue — the degraded-link case this is for.
        // Supabase only (review call): the Firestore twin is left untouched.
        if (
          getIsOnlineSnapshot() &&
          (import.meta.env.VITE_MODE === 'development' || import.meta.env.VITE_MODE === 'staging')
        ) {
          message.error('Create Many Error: ' + error.message);
        }
        console.error('createMany error:', error);
        throw error;
      }
    },
  };
};
